import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Key, 
  Trash2, 
  AlertTriangle,
  Loader2,
  CheckCircle,
  Shield,
  Bell,
  User,
  Mail,
  Smartphone,
  Clock,
  Check,
  Share2,
  Heart,
  ChevronRight,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

// Settings Help Modal Component
const SettingsHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            Account Settings
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              This page lets you manage your account security, notification preferences, and privacy settings. You can also access your referral hub from here.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Why does it matter?</h4>
            <p className="text-sm text-slate-600">
              Keeping your account secure and notifications tuned to your preference ensures you never miss an opportunity while maintaining control over your data.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {[
                'Choose "Immediate" notifications for active job seekers',
                'Your data is never browsed by employers without consent',
                'Change your password regularly for security',
                'Invite colleagues through the Referral Hub'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Notification frequency options
const EMAIL_FREQUENCY_OPTIONS = [
  {
    id: 'immediate',
    label: 'Immediate',
    description: 'Get notified as soon as a matching role comes up',
    icon: Mail
  },
  {
    id: 'weekly',
    label: 'Weekly Digest',
    description: 'Receive a consolidated email every week',
    icon: Clock
  },
  {
    id: 'biweekly',
    label: 'Bi-weekly Digest',
    description: 'Receive a consolidated email every two weeks',
    icon: Clock
  },
  {
    id: 'none',
    label: 'In-app Only',
    description: 'No emails — view matches in your dashboard',
    icon: Smartphone
  }
];

export default function AccountSettingsPage() {
  const { user, profile, authAxios, logout } = useAuth();
  const navigate = useNavigate();
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Delete account state
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Notification preferences state
  const [emailFrequency, setEmailFrequency] = useState('immediate');
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  
  // Help modal state
  const [showHelp, setShowHelp] = useState(false);

  // Load notification preferences on mount
  useEffect(() => {
    const loadNotificationPrefs = async () => {
      try {
        const response = await authAxios.get('/candidates/notification-preferences');
        if (response.data.email_frequency) {
          setEmailFrequency(response.data.email_frequency);
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
      } finally {
        setLoadingNotifications(false);
      }
    };
    loadNotificationPrefs();
  }, [authAxios]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      const formData = new FormData();
      formData.append('current_password', currentPassword);
      formData.append('new_password', newPassword);
      
      await authAxios.put('/candidates/change-password', formData);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      toast.success('Password changed successfully');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to change password';
      setPasswordError(message);
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveNotificationPrefs = async (newFrequency) => {
    setSavingNotifications(true);
    try {
      await authAxios.put('/candidates/notification-preferences', {
        email_frequency: newFrequency
      });
      setEmailFrequency(newFrequency);
      toast.success('Notification preferences saved');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save preferences';
      toast.error(message);
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeletingAccount(true);
    try {
      await authAxios.delete('/candidates/account');
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to delete account';
      toast.error(message);
    } finally {
      setDeletingAccount(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </span>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-slate-500 hover:text-lime-600"
              onClick={() => setShowHelp(true)}
              data-testid="settings-help-btn"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-2xl font-heading text-slate-900">Account Settings</h1>
          <p className="text-slate-500">Manage your account preferences and security</p>
        </div>

        <div className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <User className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Account Information</CardTitle>
                  <CardDescription>Your basic account details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-500">Email</Label>
                  <p className="text-slate-900 font-medium">{user?.email}</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-500">LinkedIn Profile</Label>
                  <p className="text-slate-900 font-medium truncate">{user?.linkedin_profile_url || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Account Status</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <CheckCircle className="h-4 w-4 text-lime-600" />
                    <span className="text-lime-600 font-medium">Email Verified</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Preferences */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-lime-100 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-lime-700" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Notification Preferences</CardTitle>
                  <CardDescription>Choose how you want to be notified about matching roles</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* In-app notification - always on */}
              <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-evergreen-950 flex items-center justify-center">
                      <Smartphone className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">In-app Notifications</p>
                      <p className="text-sm text-slate-500">View matches in your dashboard</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Check className="h-4 w-4 text-lime-600" />
                    <span>Always on</span>
                  </div>
                </div>
              </div>

              {/* Email frequency options */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Email Notifications</Label>
                <p className="text-sm text-slate-500 mb-4">
                  Choose how often you'd like to receive email updates about matching opportunities.
                </p>
                
                {loadingNotifications ? (
                  <div className="flex items-center gap-2 py-4 text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading preferences...</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {EMAIL_FREQUENCY_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isSelected = emailFrequency === option.id;
                      
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => handleSaveNotificationPrefs(option.id)}
                          disabled={savingNotifications}
                          className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                            isSelected
                              ? 'border-lime-600 bg-lime-50'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                          data-testid={`notification-${option.id}`}
                        >
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-lime-600' : 'bg-slate-100'
                          }`}>
                            <Icon className={`h-5 w-5 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${isSelected ? 'text-evergreen-950' : 'text-slate-900'}`}>
                              {option.label}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {option.description}
                            </p>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected 
                              ? 'bg-lime-600 border-lime-600' 
                              : 'border-slate-300'
                          }`}>
                            {isSelected && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {savingNotifications && (
                  <div className="flex items-center gap-2 mt-3 text-sm text-lime-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Key className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Change Password</CardTitle>
                  <CardDescription>Update your account password</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}
                {passwordSuccess && (
                  <Alert className="border-lime-200 bg-lime-50">
                    <CheckCircle className="h-4 w-4 text-lime-600" />
                    <AlertDescription className="text-lime-700">
                      Password changed successfully
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="h-11"
                    data-testid="current-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="At least 8 characters"
                    className="h-11"
                    data-testid="new-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                  <Input
                    id="confirmNewPassword"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    className="h-11"
                    data-testid="confirm-new-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-evergreen-950 hover:bg-evergreen-900"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Privacy & Data */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Privacy & Data</CardTitle>
                  <CardDescription>Your data is always under your control</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-lime-600 mt-0.5 flex-shrink-0" />
                  Your profile is never browsed by employers
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-lime-600 mt-0.5 flex-shrink-0" />
                  You decide if and when to share your identity
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-lime-600 mt-0.5 flex-shrink-0" />
                  You can delete your account and all data at any time
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Referral Hub */}
          <Card className="border-lime-200 bg-gradient-to-br from-lime-50 to-white">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-lime-100 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-lime-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading">Help Your Network</CardTitle>
                  <CardDescription>Invite colleagues who are exploring opportunities</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Know someone who'd benefit from Varyynt? Share your personal invite link and help them find their next role.
              </p>
              <Link to="/referrals">
                <Button className="bg-evergreen-950 hover:bg-evergreen-900">
                  <Share2 className="h-4 w-4 mr-2" />
                  Open Referral Hub
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Danger Zone - Delete Account */}
          <Card className="border-red-200">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-heading text-red-600">Delete Account</CardTitle>
                  <CardDescription>Permanently delete your account and all data</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Once you delete your account, all your data including your profile, preferences, 
                and resumes will be permanently removed. This action cannot be undone.
              </p>
              
              <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="destructive"
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete My Account
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-5 w-5" />
                      Delete Account
                    </DialogTitle>
                    <DialogDescription>
                      This action is permanent and cannot be undone. All your data will be deleted.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Label htmlFor="deleteConfirm" className="text-sm">
                      Type <span className="font-mono font-bold">DELETE</span> to confirm
                    </Label>
                    <Input
                      id="deleteConfirm"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      placeholder="DELETE"
                      className="mt-2"
                      data-testid="delete-confirm-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmation !== 'DELETE' || deletingAccount}
                      data-testid="confirm-delete-btn"
                    >
                      {deletingAccount ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        'Delete Account'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Settings Help Modal */}
      <SettingsHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
